import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, Empty, Tooltip, Typography } from 'antd';
import { CheckCircleOutlined, WarningOutlined } from '@ant-design/icons';
import { cardIssues, newCard } from './cardTypes';
import { cloneJson, insertAt, moveItem, removeAt, replaceAt } from './listOps';
import { AddStepButton, StepList } from './StepList';
import CardEditor from './CardEditor';
import { EditorErrorBoundary } from './EditorErrorBoundary';
import { CardType } from './validation';
import { Card, LessonBodyValue } from './types';
import './lessonBody.css';

const { Text } = Typography;

let nextStepId = 0;
const newStepId = () => `step-${++nextStepId}`;

interface Props {
  value: LessonBodyValue | null;
  onChange: (body: LessonBodyValue) => void;
  style?: React.CSSProperties;
}

/**
 * Lesson body as a list of steps (sidebar) and a form for the selected step. Only `cards`
 * is edited; any other top-level body field is carried through unchanged.
 */
const VisualBodyEditor: React.FC<Props> = ({ value, onChange, style }) => {
  const cards = useMemo(() => value?.cards ?? [], [value]);

  // Stable ids for drag-and-drop and selection, paired with cards by position. Every
  // structural edit below updates both; a body replaced from outside arrives as a remount
  // (mode switch, another lesson), so the fallback only guards against drift.
  const [stepIds, setStepIds] = useState<string[]>(() => cards.map(newStepId));
  const ids = stepIds.length === cards.length ? stepIds : cards.map((_, i) => stepIds[i] ?? `step-drift-${i}`);
  const [selectedId, setSelectedId] = useState<string | null>(ids[0] ?? null);
  const selected = Math.max(0, ids.indexOf(selectedId ?? ''));

  const issues = useMemo(() => cards.map(cardIssues), [cards]);
  const issueCounts = issues.map((list) => list.length);
  const totalIssues = issueCounts.reduce((sum, n) => sum + n, 0);

  const detailRef = useRef<HTMLDivElement>(null);
  const selectedStepId = ids[selected];
  useEffect(() => {
    if (detailRef.current) detailRef.current.scrollTop = 0;
  }, [selectedStepId]);

  const commit = (nextCards: Card[], nextIds: string[], select?: string | null) => {
    onChange({ ...(value ?? {}), cards: nextCards });
    setStepIds(nextIds);
    if (select !== undefined) setSelectedId(select);
  };

  const addCard = (type: CardType) => {
    const at = cards.length > 0 ? selected + 1 : 0;
    const id = newStepId();
    commit(insertAt(cards, at, newCard(type)), insertAt(ids, at, id), id);
  };
  const moveCard = (from: number, to: number) => commit(moveItem(cards, from, to), moveItem(ids, from, to));
  const duplicateCard = (index: number) => {
    const id = newStepId();
    commit(insertAt(cards, index + 1, cloneJson(cards[index])), insertAt(ids, index + 1, id), id);
  };
  const deleteCard = (index: number) => {
    const nextIds = removeAt(ids, index);
    commit(removeAt(cards, index), nextIds, nextIds[Math.min(index, nextIds.length - 1)] ?? null);
  };

  const jumpToNextIssue = () => {
    for (let step = 1; step <= cards.length; step++) {
      const index = (selected + step) % cards.length;
      if (issueCounts[index] > 0) {
        setSelectedId(ids[index]);
        return;
      }
    }
  };

  const addHint = cards.length > 0 ? `Insert after step ${selected + 1}` : undefined;

  return (
    <div className="lbe" style={style}>
      <div className="lbe-sidebar">
        <div className="lbe-sidebar-head">
          <Text strong>
            Steps <Text type="secondary">({cards.length})</Text>
          </Text>
          {totalIssues > 0 ? (
            <Tooltip title="Go to the next step with something to check">
              <Button size="small" type="text" icon={<WarningOutlined style={{ color: '#faad14' }} />} onClick={jumpToNextIssue}>
                {totalIssues}
              </Button>
            </Tooltip>
          ) : cards.length > 0 ? (
            <Tooltip title="Nothing to check">
              <CheckCircleOutlined style={{ color: '#52c41a' }} />
            </Tooltip>
          ) : null}
        </div>
        <div className="lbe-steps">
          <StepList
            cards={cards}
            ids={ids}
            selectedIndex={selected}
            issueCounts={issueCounts}
            onSelect={(index) => setSelectedId(ids[index])}
            onMove={moveCard}
          />
        </div>
        <div className="lbe-sidebar-foot">
          <AddStepButton block onAdd={addCard} hint={addHint} />
        </div>
      </div>

      <div className="lbe-detail" ref={detailRef}>
        {cards.length === 0 ? (
          <div className="lbe-empty">
            <Empty description="No steps yet">
              <AddStepButton onAdd={addCard} />
            </Empty>
          </div>
        ) : (
          <EditorErrorBoundary
            key={selectedStepId}
            fallback={(error) => (
              <Alert
                type="error"
                showIcon
                title="This step can't be shown in the visual editor"
                description={`${error.message} — switch to JSON (top right) to fix it.`}
              />
            )}
          >
            <CardEditor
              card={cards[selected]}
              index={selected}
              total={cards.length}
              issues={issues[selected]}
              onChange={(card) => commit(replaceAt(cards, selected, card), ids)}
              onMove={(to) => moveCard(selected, to)}
              onDuplicate={() => duplicateCard(selected)}
              onDelete={() => deleteCard(selected)}
            />
          </EditorErrorBoundary>
        )}
      </div>
    </div>
  );
};

export default VisualBodyEditor;
